import { cn } from '@/lib/cn';

/**
 * Skeleton shadcn-style. Animación pulse + bg-muted.
 * Componer con `h-X w-Y` o clases de Tailwind para definir forma/tamaño.
 */
export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-muted', className)}
      aria-hidden="true"
      {...props}
    />
  );
}

/* === Skeletons compuestos (usar donde había "Cargando…") === */

/** Skeleton de fila de tabla: 4 columnas con anchos típicos. */
export function TableRowSkeleton({ cols = 4 }: { cols?: number }) {
  const widths = ['w-32', 'w-24', 'w-20', 'w-16'];
  return (
    <div className="flex items-center gap-4 border-b border-border px-4 py-3">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} className={`h-4 ${widths[i % widths.length]}`} />
      ))}
    </div>
  );
}

/** Skeleton de tabla completa: 6 filas. */
export function TableSkeleton({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      {Array.from({ length: rows }).map((_, i) => (
        <TableRowSkeleton key={i} cols={cols} />
      ))}
    </div>
  );
}

/** Skeleton de card (reutilizado en Dashboard, Reportes). */
export function CardSkeleton() {
  return (
    <div className="rounded-lg border border-border bg-bg-elevated p-6">
      <Skeleton className="mb-2 h-4 w-1/3" />
      <Skeleton className="mb-4 h-3 w-1/2" />
      <Skeleton className="h-8 w-2/3" />
    </div>
  );
}

/** Skeleton de grid de cards (e.g. KPIs de Reportes). */
export function CardGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

/** Skeleton de header de página (título + botón). */
export function PageHeaderSkeleton() {
  return (
    <div className="mb-4 flex items-center justify-between">
      <Skeleton className="h-7 w-40" />
      <Skeleton className="h-10 w-32" />
    </div>
  );
}

/** Skeleton de página completa con tabla. */
export function TablePageSkeleton() {
  return (
    <div className="space-y-4">
      <PageHeaderSkeleton />
      <TableSkeleton />
    </div>
  );
}

/** Skeleton de página con cards (e.g. Reportes con KPIs). */
export function CardsPageSkeleton() {
  return (
    <div className="space-y-4">
      <PageHeaderSkeleton />
      <CardGridSkeleton />
    </div>
  );
}

/** Skeleton de filtros (chip + select). */
export function FiltersSkeleton() {
  return (
    <div className="flex gap-3">
      <Skeleton className="h-10 w-40" />
      <Skeleton className="h-10 w-32" />
    </div>
  );
}

/** Skeleton full-page (Suspense fallback, RequireAuth). */
export function PageFallbackSkeleton() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Skeleton className="h-8 w-8 rounded-full" />
    </div>
  );
}